import React, { useState, useEffect } from 'react'
import { Pencil } from 'lucide-react'
import axios from '../../../../config/axios'
import crosslight from '../../../../assets/stock/FiltersHeader/cross.svg'
import trash from '../../../../assets/equipment/Tables/trash.svg'
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  FormGroup,
  Label,
  TextValue,
  MaterialItem,
  ModalFooter,
  CancelButton,
  SaveButton,
  DeleteButton,
} from './style'

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
const FASHION_API_URL = `${GATEWAY_URL}/backend-fashion-orders/fashion-product`

interface GradeType {
  id: number
  name: string
}

interface Grade {
  id: number
  name: string
  typeId: number
  type?: GradeType
  GraduationType?: GradeType
}

interface ResourceCategory {
  id: number
  name: string
}

interface Resource {
  id: number
  name: string
  description: string
  categoryId: number
}

interface ViewFamilyModalProps {
  isOpen: boolean
  onClose: () => void
  onEdit: () => void
  familyId: number
}

const ViewFamilyModal: React.FC<ViewFamilyModalProps> = ({
  isOpen,
  onClose,
  onEdit,
  familyId,
}) => {
  const [loading, setLoading] = useState(true)

  const gfNameExists = (arr: any[], id: any) => {
    return arr.some(gf => (gf.Group?.name || gf.group?.name) &&
      ((gf.groupId && String(gf.groupId) === String(id)) ||
       (gf.Group?.id && String(gf.Group.id) === String(id)) ||
       (gf.group?.id && String(gf.group.id) === String(id)))
    )
  }
  const [familyData, setFamilyData] = useState<any>(null)
  const [gradesByType, setGradesByType] = useState<Record<string, Grade[]>>({})
  const [resourcesByCategory, setResourcesByCategory] = useState<Record<number, Resource[]>>({})
  const [allResourceCategories, setAllResourceCategories] = useState<ResourceCategory[]>([])
  const [collectionMaterials, setCollectionMaterials] = useState<any[]>([])
  const [collectionColorsAvailable, setCollectionColorsAvailable] = useState<{ id: number; name: string; hex?: string }[]>([])
  const [selectedCollectionColorIds, setSelectedCollectionColorIds] = useState<number[]>([])
  const [fabrics, setFabrics] = useState<any[]>([])
  const [selectedGradeIds, setSelectedGradeIds] = useState<number[]>([])
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([])


  useEffect(() => {
    if (!isOpen) {
      setFamilyData(null)
      setGradesByType({})
      setResourcesByCategory({})
      setAllResourceCategories([])
      setCollectionMaterials([])
      setCollectionColorsAvailable([])
      setSelectedCollectionColorIds([])
      setFabrics([])
      setSelectedGradeIds([])
      setSelectedGroupIds([])
    }
  }, [isOpen])

  const handleDeleteFamily = async () => {
    if (!window.confirm('Tem certeza que deseja excluir esta família?')) return

    const token = localStorage.getItem('token')
    if (!token) {
      alert('Sessão expirada. Faça login novamente.')
      return
    }

    try {
      await axios.delete(`${FASHION_API_URL}/families/${familyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      alert('Família excluída com sucesso!')
      onClose()
      window.location.reload()
    } catch (err: any) {
      console.error('Erro ao excluir família:', err)
      alert(
        err.response?.data?.message ||
          'Erro ao excluir família. Tente novamente.',
      )
    }
  }

  useEffect(() => {

  const fetchFamilyData = async () => {
      if (!isOpen) return

      setLoading(true)
      const token = localStorage.getItem('token')
      const headers = { Authorization: `Bearer ${token}` }

      try {
        const familyRes = await axios.get(
          `${FASHION_API_URL}/families/${familyId}`,
          { headers }
        )

        const family = familyRes.data?.data || familyRes.data
        console.debug('raw family response', family)



        const hasGrades =
          (Array.isArray(family.GradeInFamily) && family.GradeInFamily.length > 0) ||
          (Array.isArray(family.grades) && family.grades.length > 0) ||
          (Array.isArray(family.FamilyGrade) && family.FamilyGrade.length > 0) ||
          (Array.isArray(family.familyGrades) && family.familyGrades.length > 0)
        if (!hasGrades) {
          try {
            const gradesRes = await axios.get(`${FASHION_API_URL}/families/${familyId}/grades`, { headers })
            const arr = Array.isArray(gradesRes.data) ? gradesRes.data : gradesRes.data?.data || []
            family.GradeInFamily = arr
            console.debug('fetched family grades array', arr)
          } catch (e) {
            // ignorar falha ao buscar graduações
          }
        }
        console.debug('after grade fetch family.GradeInFamily', family.GradeInFamily)



        console.debug('initial family.FamilyColor', family.FamilyColor)


        const ensureGroupNames = async () => {
          if (Array.isArray(family.GroupInFamily) && family.GroupInFamily.length > 0) {
            const missingIds: number[] = Array.from(
              new Set(
                family.GroupInFamily
                  .map((gf: any) => gf.groupId || gf.Group?.id || gf.group?.id)
                  .filter((id: any) => id && !gfNameExists(family.GroupInFamily, id)),
              ),
            ) as number[]
            if (missingIds.length > 0) {
              await Promise.all(
                missingIds.map(async (id: number) => {
                  try {
                    const r = await axios.get(`${FASHION_API_URL}/groups/${id}`, { headers })
                    const data = r.data?.data || r.data
                    family.GroupInFamily.forEach((gf: any) => {
                      const gid = gf.groupId || gf.Group?.id || gf.group?.id
                      if (gid === id) {
                        if (!gf.Group) gf.Group = {}
                        gf.Group.name = data.name
                        if (!gf.group) gf.group = {}
                        gf.group.name = data.name
                      }
                    })
                  } catch (err) {
                    // ignorar erro ao buscar nome de grupo individual
                  }
                }),
              )
            }
          }





          const singleGroupId =
            family.groupId || family.Group?.id || family.group?.id
          if (
            singleGroupId &&
            (!Array.isArray(family.GroupInFamily) || family.GroupInFamily.length === 0) &&
            !(family.groups && family.groups.length > 0)
          ) {
            try {
              const r = await axios.get(`${FASHION_API_URL}/groups/${singleGroupId}`, { headers })
              const data = r.data?.data || r.data
              family.Group = family.Group || {}
              family.Group.name = data.name
              family.group = family.group || {}
              family.group.name = data.name
            } catch (err) {
              // ignorar erro ao buscar único grupo
            }
          }


          if (Array.isArray(family.groups) && family.groups.length > 0) {
            const missing = family.groups
              .filter((g: any) => g.id && !g.name)
              .map((g: any) => g.id)
            if (missing.length > 0) {
              await Promise.all(
                missing.map(async (id: number) => {
                  try {
                    const r = await axios.get(`${FASHION_API_URL}/groups/${id}`, { headers })
                    const data = r.data?.data || r.data
                    family.groups.forEach((g: any) => {
                      if (g.id === id) g.name = data.name
                    })
                  } catch (_err) {
                    // ignorar falha ao buscar nome de grupo faltante
                  }
                }),
              )
            }
          }
        }

        await ensureGroupNames()
        console.debug('after ensureGroupNames, GroupInFamily:', family.GroupInFamily, 'groups:', family.groups)

        setFamilyData(family)
        console.debug('final family data arrays', {
          grades: family.GradeInFamily || family.grades,
          groups: family.GroupInFamily || family.groups,
          colors: family.FamilyColor || family.familyColor,
        })


        const familyGroupIds = (
          (family.GroupInFamily || family.groups || []) as any[]
        )
          .map((gf: any) => gf.groupId || gf.id || gf.Group?.id || gf.group?.id)
          .filter((id: any) => id)
        setSelectedGroupIds(familyGroupIds)

        console.debug('family groups', familyGroupIds, 'grade array', family.GradeInFamily || family.grades)

        try {
          const collectionId = family?.collectionId || family?.Collection?.id || family?.collection?.id
          if (collectionId) {
            try {
              const materialsRes = await axios.get(`${FASHION_API_URL}/collections/${collectionId}/materials`, { headers })
              const materials = Array.isArray(materialsRes.data) ? materialsRes.data : (materialsRes.data?.data || [])
              setCollectionMaterials(materials)
            } catch (err) {
              setCollectionMaterials([])
            }

            try {
              const colRes = await axios.get(`${FASHION_API_URL}/collections/${collectionId}`, { headers })
              const colData = colRes.data?.data || colRes.data
              const cols = Array.isArray(colData?.CollectionColor) ? colData.CollectionColor.map((c: any) => ({ id: c.id, name: c.name, hex: c.hex })) : []
              setCollectionColorsAvailable(cols)
              const familyColorIds = (family.FamilyColor || family.familyColor || []).map((fc: any) => fc.collectionColorId)
              setSelectedCollectionColorIds(familyColorIds)
            } catch (err) {
              setCollectionColorsAvailable([])
              setSelectedCollectionColorIds([])
            }


            const fabricItems: any[] = (family.FamilyMaterial || family.materials || []).map((fm: any, index: number) => {
              const cm = fm.CollectionMaterial || fm.collectionMaterial || {}
              return {
                id: index + 1,
                selectedCollectionMaterialId: fm.collectionMaterialId,
                selectedMaterialName: cm.Material?.name || cm?.material?.name || fm.name || '',
                resourceIds: (fm.FamilyMaterialResources || fm.resources || []).map((r: any) => r.resourceId || r.id),
              }
            })
            setFabrics(fabricItems)

            const familyGradeIds = (family.GradeInFamily || family.grades || []).map((fg: any) => fg.gradeId || fg.id) || []
            setSelectedGradeIds(familyGradeIds)
          }
        } catch (e) {
          console.error('Erro ao popular dados adicionais da família:', e)
        }


        try {
          const gradesRes = await axios.get(`${FASHION_API_URL}/grades`, { headers })
          const grades: Grade[] = Array.isArray(gradesRes.data) ? gradesRes.data : []

          const byType: Record<string, Grade[]> = {}
          grades.forEach((grade) => {
            const typeName = (grade.GraduationType || grade.type)?.name
            if (!typeName) return
            if (!byType[typeName]) {
              byType[typeName] = []
            }
            byType[typeName].push(grade)
          })

          Object.keys(byType).forEach((typeName) => {
            byType[typeName].sort((a, b) => {
              const aNum = parseInt(a.name)
              const bNum = parseInt(b.name)
              if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum
              return a.name.localeCompare(b.name)
            })
          })

          setGradesByType(byType)
        } catch (gradesError) {
          console.error('⚠️ Erro ao carregar grades:', gradesError)

        }

        try {
          const categoriesRes = await axios.get(`${FASHION_API_URL}/resource-categories`, { headers })
          const categories: ResourceCategory[] = Array.isArray(categoriesRes.data) ? categoriesRes.data : []
          setAllResourceCategories(categories)

          const resourcesRes = await axios.get(`${FASHION_API_URL}/resources`, { headers })
          const resources: Resource[] = Array.isArray(resourcesRes.data) ? resourcesRes.data : []

          const byCategory: Record<number, Resource[]> = {}
          resources.forEach((resource) => {
            if (resource.categoryId) {
              if (!byCategory[resource.categoryId]) {
                byCategory[resource.categoryId] = []
              }
              byCategory[resource.categoryId].push(resource)
            }
          })
          setResourcesByCategory(byCategory)
        } catch (resourcesError) {
          console.error('⚠️ Erro ao carregar recursos:', resourcesError)

        }
      } catch (error: any) {
        console.error('❌ Erro ao carregar dados da família:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchFamilyData()
  }, [isOpen, familyId])

  if (!isOpen) return null

  if (loading) {
    return (
      <ModalOverlay onClick={onClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <p style={{ padding: '20px', textAlign: 'center' }}>Carregando dados...</p>
        </ModalContainer>
      </ModalOverlay>
    )
  }

  if (!familyData) {
    return (
      <ModalOverlay onClick={onClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <p style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
            Erro ao carregar família
          </p>
        </ModalContainer>
      </ModalOverlay>
    )
  }


  const familyGradesArray =
    familyData.GradeInFamily ||
    familyData.gradeInFamily ||
    familyData.grades ||
    familyData.Grades ||
    familyData.FamilyGrade ||
    familyData.familyGrades ||
    []
  const familyGradeIdsFromData = (Array.isArray(familyGradesArray) ? familyGradesArray.map((fg: any) => fg.gradeId || fg.grade?.id || fg.id) : []) || []

  const familyGradeIds = (selectedGradeIds && selectedGradeIds.length > 0) ? selectedGradeIds : familyGradeIdsFromData
  const gradeTypeGroups: Record<string, Grade[]> = {}


  Object.entries(gradesByType).forEach(([typeName, gradeList]) => {
    const filtered = gradeList.filter((g) => familyGradeIds.includes(g.id))
    if (filtered.length > 0) {
      gradeTypeGroups[typeName] = filtered
    }
  })

  const gradeTypeOrder: string[] = Object.keys(gradeTypeGroups)



  let groupNames: string[] = []
  if (Array.isArray(familyData.GroupInFamily) && familyData.GroupInFamily.length > 0) {
    groupNames = familyData.GroupInFamily
      .map((gf: any) => gf.Group?.name || gf.group?.name || '')
      .filter(Boolean)
  } else if (Array.isArray(familyData.groups) && familyData.groups.length > 0) {
    groupNames = familyData.groups.map((g: any) => g?.name || '').filter(Boolean)
  }

  if (groupNames.length === 0) {
    const singleId = familyData.groupId || familyData.Group?.id || familyData.group?.id
    if (singleId) {
      groupNames = [String(singleId)]
    }
  }
  const hasExplicitGroupName =
    groupNames.length > 0 ||
    familyData.Group?.name ||
    familyData.group?.name
  const groupDisplay = hasExplicitGroupName
    ? (groupNames.length > 0
        ? groupNames.join(', ')
        : familyData.Group?.name || familyData.group?.name)
    : null
  const resourceMap: Record<number, string> = {}
  Object.values(resourcesByCategory).forEach((arr) => {
    arr.forEach((r) => {
      resourceMap[r.id] = r.name
    })
  })


  const allGradesFlat: Grade[] = Object.values(gradesByType).flat()
  const gradeNameMap: Record<number, string> = {}
  allGradesFlat.forEach((g) => { gradeNameMap[g.id] = g.name })

  console.debug('groupDisplay computed:', groupDisplay, 'gradeTypeOrder:', gradeTypeOrder)

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Detalhes da família</ModalTitle>
          <CloseButton onClick={onClose}>
            <img src={crosslight} alt='Close' />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <FormGroup>
            <Label>Coleção</Label>
            <TextValue>{familyData.Collection?.name || familyData.collection?.name || 'N/A'}</TextValue>
          </FormGroup>

          <FormGroup>
            <Label>Nome da família</Label>
            <TextValue>{familyData.name || 'N/A'}</TextValue>
          </FormGroup>

          
          {groupDisplay ? (
            <FormGroup>
              <Label>Grupo</Label>
              <TextValue>{groupDisplay}</TextValue>
            </FormGroup>
          ) : (
            <FormGroup>
              <Label>Grupo</Label>
              <TextValue>Nenhum</TextValue>
            </FormGroup>
          )}

          
          {}
          <FormGroup>
            <Label>Cores</Label>
            <TextValue>{
              ((familyData.FamilyColor || familyData.familyColor || []) as any[])
                .map((fc: any) => fc.CollectionColor?.name || fc.collectionColor?.name || fc.name)
                .filter(Boolean)
                .join(', ') || 'Nenhuma'
            }</TextValue>
          </FormGroup>

          
          {gradeTypeOrder.length > 0 ? (
            <>
              <FormGroup>
                <Label>Tipo de graduação</Label>
                <TextValue>{gradeTypeOrder.join(', ')}</TextValue>
              </FormGroup>

              {gradeTypeOrder.map((typeName) => {
                const grades = gradeTypeGroups[typeName] || []
                return (
                  <FormGroup key={typeName}>
                    <Label>Graduação - {typeName}</Label>
                    <TextValue>{grades.map((grade) => grade.name).join(', ')}</TextValue>
                  </FormGroup>
                )
              })}
            </>
          ) : (
            <FormGroup>
              <Label>Graduação</Label>
              <TextValue>Nenhuma</TextValue>
            </FormGroup>
          )}

          
          {fabrics && fabrics.length > 0 && (
            <FormGroup>
              <Label>Tecidos e Aviamentos</Label>
              <div>
                {fabrics.map((f: any, i: number) => {
                  const resourceNames = (f.resourceIds || []).map((id: number) => resourceMap[id]).filter(Boolean)
                  const resourcesText = resourceNames.length ? ` - Insumos: ${resourceNames.join(', ')}` : ''
                  return (
                    <MaterialItem key={i}>{(f.selectedMaterialName || f.name || 'N/A') + resourcesText}</MaterialItem>
                  )
                })}
              </div>
            </FormGroup>
          )}

          

          {(familyData.materials || familyData.Materials) && (familyData.materials || familyData.Materials).length > 0 && (
            <FormGroup>
              <Label>Tecidos e Insumos</Label>
              <div>
                {(familyData.materials || familyData.Materials).map((material: any, index: number) => {

                  const cm = material.collectionMaterial || material.CollectionMaterial || material.collection_material
                  const mv = cm?.materialVariation || cm?.material_variation || cm?.MaterialVariation
                  const stock = mv?.stock || mv?.Stock || mv?.stock?.[0]
                  const stockName = stock?.description || material.name || 'N/A'
                  const colorName = mv?.color?.name || cm?.color?.name || ''
                  const materialName = colorName ? `${stockName} - ${colorName}` : stockName

                  const materialResourceIds = (material.resources || material.Resources || []).map((r: any) => r.resourceId || r.id)
                  const resourceParts: string[] = []
                  allResourceCategories.forEach((category) => {
                    const resources = resourcesByCategory[category.id] || []
                    const selectedResources = resources.filter((r) =>
                      materialResourceIds.includes(r.id)
                    )

                    if (selectedResources.length > 0) {
                      const categoryName = category.name
                      const resourceNames = selectedResources.map((r) => r.name).join(', ')
                      resourceParts.push(`${categoryName}: ${resourceNames}`)
                    }
                  })

                  const fullText = resourceParts.length > 0
                    ? `${materialName} - ${resourceParts.join(' - ')}`
                    : materialName

                  return (
                    <MaterialItem key={index}>
                      {fullText}
                    </MaterialItem>
                  )
                })}
              </div>
            </FormGroup>
          )}
        </ModalContent>

        <ModalFooter>
          <DeleteButton onClick={handleDeleteFamily}>
            <img src={trash} alt='Excluir família' />
            Excluir família
          </DeleteButton>
          <SaveButton onClick={onEdit} style={{ marginLeft: '1rem' }}>
            <Pencil />
            Editar família
          </SaveButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  )
}

export default ViewFamilyModal